import Cookies from 'js-cookie';
import { currentUser } from '@/helpers/user-helper';
import { loadOnBoardingStatus } from '@/helpers/onboarding-helper';
import { COUNTRY_EXCEPTIONS } from '@/constants/web-locales.constant';
import { WebLocale } from '@/enums/web-locale';
import { getUserLocationCountryCode } from '@/helpers/geo-location-helper';
import { storeSrDebuggerInfo } from '@/features/sponsored-recommendations/helpers/sponsoredrec-debugging-tools';
import { storeUserGeoLocation } from '@/helpers/geo-location-helper';
import { storeNoIndexDebug } from '@/helpers/query-parameters-helper';
import { getCurrentScope, setUser } from '@sentry/nuxt';
import { captureMessageForSentry, setupSentry } from '@/libraries/sentry';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { generateJwId, generateUUIDv4 } from '@/helpers/uuid-helper';
import { GlobalComponentsPlugin } from '@/plugins/global-components';
import { GlobalDirectivesPlugin } from '@/plugins/global-directives';
import { globalRoutes } from '@/routing/global';
import { detectDomain, Domain, handleSubdomainSwitch } from '@/routing/helpers/subdomain-helper';
import { useAppStore } from '@/stores/pinia/useAppStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { getApps, initializeApp } from 'firebase/app';
import { withRetry } from '@/helpers/retry-helper';
import { getUserCustomToken } from '@/helpers/user-helper';
import { ApolloLink } from '@apollo/client/core';
import { map } from 'rxjs';
import { useCookie } from '#app';
import { initArbitrage } from '@/helpers/composables/useArbitrage';
import { applyRoute } from '@/helpers/state-helper';
import { activeSubgenreCountriesWebLocales } from '@/constants/subgenres';
import { useFooterStore } from '@/stores/pinia/useFooterStore';
import { createRoutes } from '~/routing';
import { RouterMeta } from '@/routing/config';
import { createVfm } from 'vue-final-modal';
import { getClientLinks, getServerLinks } from '@/graphql/utils';

const MAX_AGE = 30 * 24 * 60 * 60 * 1000; // 30 Days
const JWID_COOKIE = 'jw_id' as const;

if (process.env.NODE_ENV !== 'production') {
	const originalWarn = console.warn;
	console.warn = (...args) => {
		if (args[0]?.includes?.('Write operation failed')) {
			console.trace('Vue readonly computed write:');
		}
		if (typeof args[0] === 'string' && args[0].includes('[Vue Router warn]')) {
			return;
		}
		originalWarn(...args);
	};
}

export default defineNuxtPlugin({
	dependsOn: ['pinia-init'],
	async setup(nuxtApp) {
		const context = useNuxtApp();
		const url = useRequestURL().pathname;
		const runtimeConfig = useRuntimeConfig();

		const headers = useRequestHeaders();

		// Per-phase SSR timing — mirrors the `phase()` helper that lived in src/main.ssr.ts on `main`.
		// Phases are attached to `event.context.ssrPhases` and observed as
		// `ssr_phase_duration_seconds{phase=...}` by server/plugins/prometheus.ts.
		// All durations are milliseconds; conversion to seconds happens at observation time.
		const phases: Record<string, number> = {};
		const t = (): bigint => (import.meta.server ? process.hrtime.bigint() : 0n);
		const setupStart = t();
		function phase(label: string, start: bigint) {
			if (!import.meta.server) return;
			phases[label] = Number(t() - start) / 1e6;
		}

		// Per-request log of GraphQL operation intervals (absolute hrtime ms), populated by a
		// timing Apollo link below. Used in the `app:rendered` hook to split the opaque `render`
		// phase into network-wait (GraphQL) vs. CPU (renderToString).
		const gqlIntervals: Array<[number, number]> = [];

		nuxtApp.vueApp.config.performance = process.env.NODE_ENV === 'production';
		nuxtApp.vueApp.config.errorHandler = (error, _, info) => {
			// mObs - next steps: sentry may not be initialized yet here, consider a queue system to avoid lost errors
			captureMessageForSentry(
				'[Uncaught Error]:',
				{ where: '[main.ssr.ts]: Vue Error Handler', error: error as Error, meta: { info } },
				'error'
			);
		};

		globalThis.IS_TOUCH = import.meta.client ? !window.matchMedia('(hover: hover)').matches : false;
		nuxtApp.vueApp.use(GlobalComponentsPlugin);
		nuxtApp.vueApp.use(GlobalDirectivesPlugin);
		nuxtApp.vueApp.use(createVfm());

		// START: CLS Check
		if (process.env.NODE_ENV !== 'production') {
			import('@/plugins/cls-check').then(module => nuxtApp.vueApp.use(module.ClsCheckPlugin));
		}
		// END: CLS Check

		if (import.meta.client) {
			setInterval(checkHydrationBails, 1000);
		}

		let s = t();
		const didGenerateId = initJwId(runtimeConfig.public.jwConfig.GRAPHQL_URL, runtimeConfig.public.jwConfig.DOMAIN);
		phase('init-jw-id', s);

		s = t();
		if (getApps().length === 0) {
			try {
				initializeApp({
					apiKey: runtimeConfig.public.jwConfig.FIREBASE_API_KEY,
					authDomain: runtimeConfig.public.jwConfig.FIREBASE_AUTH_DOMAIN,
					projectId: runtimeConfig.public.jwConfig.FIREBASE_PROJECT_ID,
					storageBucket: runtimeConfig.public.jwConfig.FIREBASE_STORAGE_BUCKET,
					messagingSenderId: runtimeConfig.public.jwConfig.FIREBASE_MESSAGING_SENDER_ID,
					appId: runtimeConfig.public.jwConfig.FIREBASE_APP_ID,
				});
			} catch (err) {
				const error: any = err;
				captureMessageForSentry(
					'Firebase Init Issue:',
					{ error, where: '[main.common.ts] initFirebase()' },
					'error'
				);
			}
		}
		phase('init-firebase', s);
		if (BUILD_CONFIG.globals.BUILD_TARGET === 'SSR') {
			// Make basic SSR init before init Store
			let currentXServeTrack = null;

			if (import.meta.server) {
				// get canary flag, e.g. canary-alpha if present
				currentXServeTrack = headers['x-serve-track'] || null;
			}

			context.provide('ssrData', {
				isSeoUser: didGenerateId,
				xServeTrack: currentXServeTrack,
			});
		}

		s = t();
		const appStore = useAppStore(context.$pinia);
		const constantStore = useConstantStore(context.$pinia);
		const filterStore = useFilterStore(context.$pinia);
		const languageStore = useLanguageStore(context.$pinia);
		const routingStore = useRoutingStore(context.$pinia);
		const trackingStore = useTrackingStore(context.$pinia);
		const userStore = useUserStore(context.$pinia);
		phase('stores-init', s);

		if (import.meta.client) {
			await storeUserGeoLocation();
			storeSrDebuggerInfo();

			// temp solution to set debug params in session storage in meta.store
			storeNoIndexDebug();
		}

		const path = url.split('?')[0]!;
		const slug = /^\/[\w\d]+/.exec(url)?.[0] || '';
		const isGlobalRoute = globalRoutes.includes(path) || globalRoutes.includes(slug);

		if (isGlobalRoute) {
			let locale =
				userStore.persistedWebLocale || ((import.meta.client && languageStore.detectedWebLocale) as string);

			// For Global routes we still need to know the location in the server
			// if server OR if SPA app
			if (!locale && import.meta.server) {
				// This header is only meant for global routes because the region is not in the URL
				// IN SSR, DO NOT PASS THIS TO CLIENT (use `getUserLocationCountryCode` instead)
				const region = headers['x-client-geo-region'];
				locale = region?.toLowerCase()!;
			}

			// get Browser Locale as last resort
			// happens for SPA visitors that do not pass through SSR first
			if (!locale && import.meta.client) {
				locale = getUserLocationCountryCode()?.toLowerCase() as WebLocale;
			}

			// hard fallback to US just in case
			if (!locale) locale = 'us';

			languageStore.setDetectedWebLocale(locale);
		} else {
			const locale = path && path.length >= 3 && path.split('/')[1];

			const webLocale = locale && COUNTRY_EXCEPTIONS[locale] ? COUNTRY_EXCEPTIONS[locale] : locale;

			languageStore.setDetectedWebLocale(webLocale as WebLocale);
		}

		context.provide('webLocale', languageStore.webLocale);

		s = t();
		const req = nuxtApp.ssrContext?.event?.node?.req;

		if (import.meta.server && req) {
			let hostname =
				headers.site || headers.host || headers['x-forwarded-host'] || headers['x-original-host'] || '';

			if (!hostname && req.url) {
				if (headers.referer) {
					try {
						const refUrl = new URL(headers.referer);
						hostname = refUrl.hostname;
					} catch (e) {}
				}

				// Fallback: check if we're being crawled by a bot that might include domain info in User-Agent or other headers
				if (!hostname) {
					const userAgent = headers['user-agent'] || '';

					if (userAgent.toLowerCase().includes('bot')) {
						// Check if referer contains any known subdomain pattern
						const referer = headers.referer || '';
						const knownSubdomains = Object.values(Domain).filter(domain => domain !== Domain.MAIN);

						// Look for any known subdomain in the referer
						const matchedSubdomain = knownSubdomains.find(subdomain => referer.includes(`${subdomain}.`));

						if (matchedSubdomain) {
							hostname = `${matchedSubdomain}.${runtimeConfig.public.jwConfig.DOMAIN}`;
						} else {
							hostname = `www.${runtimeConfig.public.jwConfig.DOMAIN}`;
						}
					}
				}
			}

			routingStore.setActiveDomain(detectDomain(hostname as string));
		} else if (import.meta.client) {
			routingStore.setActiveDomain(detectDomain(window.location.hostname));
		}
		phase('domain-detection', s);

		const parallelFetches: Promise<unknown>[] = [];
		const initLanguagePromise = languageStore.initLanguage();
		parallelFetches.push(initLanguagePromise);
		if (import.meta.server || !runtimeConfig.public.ssrMode) {
			parallelFetches.push(constantStore.fetchPackages());
		}

		if (import.meta.server && userStore.jwId && !url.includes('/refresh')) {
			parallelFetches.push(userStore.loadSettings());
		}

		s = t();
		const routes = createRoutes(languageStore.webLocale, routingStore.activeDomain);
		routes.forEach(route => context.$router.addRoute(route));
		phase('routes-create', s);

		// Initialize Sentry early with router to catch all initialization errors
		if (import.meta.client) {
			setupSentry({
				router: context.$router,
				jwId: userStore.jwId,
				xServeTrack: appStore.xServeTrack ?? null,
				isSeoUser: context?.$ssrData?.isSeoUser,
				isLoggedIn: userStore.isLoggedIn,
				buyboxView: userStore.newBuyboxView,
				buyboxExpView: userStore.buyboxExpView,
				buyboxPresentationType: userStore.buyboxPresentationType ?? undefined,
				buyboxTooltipAcknowledged: userStore.newBuyboxTooltipAcknowledged,
				country: languageStore.country,
				newBuyboxFilterType: userStore.newBuyboxFilterType,
				onboardingStatus: userStore.onboardingStatus ?? undefined,
			});
		}

		// Set server-side Sentry context once stores are initialized.
		// Supplements the preHandler cookie-based user set in sentry-server.js —
		// covers SEO/first-time users who have no jw_id cookie yet, and adds web_locale.
		if (import.meta.server) {
			getCurrentScope().setTags({
				web_locale: languageStore.webLocale,
				auth_status: userStore.isLoggedIn ? 'logged_in' : 'logged_out',
			});

			if (userStore.jwId) {
				setUser({ id: userStore.jwId });
			}
		}

		// Monitor JS bundle sizes for Googlebot 2MB limit
		if (import.meta.client) {
			import('@/helpers/bundle-size-monitor').then(({ initBundleSizeMonitor }) => initBundleSizeMonitor());
		}

		// Make current domain available to all Vue components for SmartLink domain detection
		context.provide('domain', routingStore.activeDomain);
		s = t();
		await initLanguagePromise;
		phase('await-init-language', s);
		context.provide('language', languageStore.overriddenLanguage);

		if (import.meta.server || !runtimeConfig.public.ssrMode) {
			parallelFetches.push(constantStore.fetchConstants());
			if (activeSubgenreCountriesWebLocales.includes(languageStore.webLocale)) {
				parallelFetches.push(constantStore.fetchSubgenres());
			}
			const footerStore = useFooterStore(context.$pinia);
			// all requests that are used on pretty much all pages and are un-personalised and cacheable
			// making sure that we got all uncritical requests done before we ship HTML back
			parallelFetches.push(footerStore.fetchTopTitles());
			parallelFetches.push(footerStore.fetchTopArticles());
		}

		TrackingHelper.resetState();

		if (import.meta.client) {
			// jwId client handover needs to happen before we await for refreshTokenPromise,
			// because if there is an error with the refresh token, it might await endlessly
			const jwId =
				// If we already have a jwId in the store (e.g., synced from cookies on subdomains)
				// then it is the most up to date
				userStore.jwId ??
				// otherwise check the cookie as the cross-domain source of truth
				Cookies.get(JWID_COOKIE) ??
				// finally fallback to localStorage
				JSON.parse(localStorage.getItem('jw/user') ?? 'null')?.jwId;

			userStore.setJwId(jwId);

			if (userStore.isLoggedIn) {
				// Wait for the refreshToken promise to finish Apollo init (it needs a fresh access token).
				// This order is important, we want to try refresh the token before initializing the store
				// and making the requests to loadList and loadSettings. The user would be logged out if the refreshToken fails
				const token = await userStore.refreshToken();

				// If token isn't returned then there was an error returning token
				if (!token) {
					context.provide('showRefreshTokenError', true);

					TrackingHelper.trackEvent('refresh_token_error', {
						action: 'main_ssr_refresh',
					});
				}
			}

			if (userStore.accessToken) {
				const user = await currentUser();
				if (user == null) {
					const accessToken = userStore.accessToken;
					const jwId = userStore.jwId as string;

					let allRetriesFailed = false;
					let finalError: Error | undefined;

					const maxAttempts = 3;

					await withRetry(
						async () => getUserCustomToken(accessToken, jwId),
						async ({ attempt, maxAttempts, error }) => {
							const message =
								error == null
									? `[main.ssr.ts] getUserCustomToken returned undefined (attempt ${attempt}/${maxAttempts})`
									: `[main.ssr.ts] getUserCustomToken threw error (attempt ${attempt}/${maxAttempts})`;

							captureMessageForSentry(
								message,
								{
									error,
									accessToken: accessToken?.substring(0, 20) + '...',
									jwId,
									where: 'main.ssr.ts initFirebaseUser',
								},
								attempt >= maxAttempts ? 'error' : 'warning'
							);

							if (attempt >= maxAttempts) {
								allRetriesFailed = true;
								finalError = error;
							}
						},
						{ maxAttempts }
					);

					if (allRetriesFailed) {
						captureMessageForSentry(
							'[main.ssr.ts] Failed to establish Firebase session after retries - logging out',
							{
								error: finalError,
								jwLoginId: userStore.jwLoginId,
								attempts: maxAttempts,
								where: 'main.ssr.ts initFirebaseUser',
							},
							'error'
						);

						await userStore.logout();
					}
				}
			}
		}

		// Create the apollo client
		s = t();
		let links: ApolloLink[] = [];
		if (import.meta.server) {
			/**
			 * Backend Tracking
			 * Forward HTTP request user-agent to apollo requests
			 * Used for Tracking Mutations
			 **/
			const extraHeaders: Record<string, any> = {};

			if (runtimeConfig.rateLimitShortcut != null) {
				extraHeaders['X-Rate-Limit-Shortcut'] = runtimeConfig.rateLimitShortcut;
			}

			const UserAgent = headers['user-agent'];
			if (UserAgent && UserAgent.indexOf('GoogleStackdriverMonitoring-UptimeChecks') === -1) {
				extraHeaders['x-forwarded-user-agent'] = UserAgent;
			}

			const contextXServeTrack = context?.$ssrData?.xServeTrack;
			if (contextXServeTrack) {
				extraHeaders['x-environment'] = contextXServeTrack;
			}

			links = await getServerLinks({ pinia: context.$pinia, headers: { ...headers, ...extraHeaders } });
			// Front of the chain so its forward() wraps every downstream link incl. HTTP,
			// capturing each operation's full in-flight wall-clock for the render split.
			links.unshift(makeGraphqlTimingLink(gqlIntervals));
		} else {
			const extraHeaders: Record<string, any> = {};
			if (appStore.xServeTrack) {
				extraHeaders['x-environment'] = appStore.xServeTrack;
			}

			links = await getClientLinks({ pinia: context.$pinia, headers: { ...headers, ...extraHeaders } });
		}

		const $apollo = useNuxtApp().$apolloClients.default!;
		$apollo.setLink(ApolloLink.from(links));
		$apollo.devtoolsConfig.enabled = process.env.NODE_ENV !== 'production';
		phase('apollo-links', s);

		if (BUILD_CONFIG.globals.BUILD_TARGET === 'SPA') {
			// initialize id service
			if (userStore.jwId) {
				context.provide('ssrData', {
					...(context.$ssrData ?? {}),
					isSeoUser: false,
				});
				userStore.setJwId(userStore.jwId);
			} else {
				context.provide('ssrData', {
					...(context.$ssrData ?? {}),
					isSeoUser: true,
				});
				await userStore.fetchJwId($apollo);
			}
		}

		/*    
	//TODO NUXT: support SERVER_ENV
	if (import.meta.server) {
		// clear SERVER_ENV to avoid leaks
		delete context.ssr.data.SERVER_ENV;
	} */

		appStore.setSeoUser(context.$ssrData.isSeoUser);

		if (import.meta.server) {
			// xServeTrack is only available on the server
			// context.ssrData is overwritten by the client
			appStore.setXServeTrack(context.$ssrData.xServeTrack);
		}

		// NOTE(valerio.mazza): failsafe for useFilterStore to remove after a proper refactor of the useFilterStore
		if (filterStore.getWebLocale(languageStore.webLocale) == null) {
			// init filter here before setting webLocale for reactivity reason (webLocale is watched everywhere)
			filterStore.initFiltersPerLocale(languageStore.webLocale);
		}

		if (import.meta.client && userStore.jwId && !url.includes('/refresh')) {
			await userStore.loadSettings();
		}

		// check for first routing and possibly update store state from url (e.g. filters from url)
		context.$router.beforeEach(async (to, from) => {
			const firstRouting = from?.name === undefined || from?.name === 'app.refreshtoken';

			// Handle route changes that switch subdomain
			if (import.meta.client && to.name) {
				await handleSubdomainSwitch({ pinia: context.$pinia }, to);
			}

			// persist scroll position for each tab
			if (!firstRouting && import.meta.client) {
				const tab = (
					from?.meta?.tab === 'sports' ? `${from?.meta?.tab}-${from?.meta?.data}` : from?.meta?.tab
				) as RouterMeta['tab'];

				routingStore.setScrollPosition({ tab, y: window.scrollY });
			}
			if (firstRouting && import.meta.client) {
				initArbitrage(to, languageStore.country);
			}

			// Track page view on client only
			if (import.meta.client) {
				const pageViewUUID = generateUUIDv4();
				trackingStore.setPageViewUUID(pageViewUUID);

				const { meta, fullPath } = to;

				// ###### TRACK PAGE ######
				// We deliberately do not `await trackPage` here, to avoid blocking the router
				// on slow async work. Instead, we ensure critical prerequisites (like urlMetadata)
				// are ready before calling it.
				// Here the async actions that happen in trackPage, that are already resolved beforehand and don't yield additional awaits:
				// - urlMetadata: resolved
				// - getContexts: only unresolved if there is an unknown pagetype
				const metaContexts = (meta?.contexts || []) as string[];

				// we track page views ourselves for title detail pages and (nearly all) sport pages
				const sportPageExceptions = [
					// 'app.sports.subdomain.all', // this isn't included because it doesn't have any sport context
					'app.sports.subdomain.overview',
					'app.sports.subdomain.competition', // @todo implement with sport context
					'app.sports.subdomain.event',
					'app.sports.subdomain.team|competition',
				];

				//handle first routing subgenre in routingConfig, assign permanentAudienceSubgenre in beforeEnter() subgenre route
				const firstRoutingIsSubgenre =
					(from?.name === null || from?.name === 'app.refreshtoken') &&
					((to.name as string) || '').includes('subgenre');

				if (
					!metaContexts.includes('title') &&
					!sportPageExceptions.some(prefix => ((to.name as string) || '').startsWith(prefix)) &&
					!firstRoutingIsSubgenre
				) {
					TrackingHelper.trackPage(fullPath);
				}
			}

			// check first page view
			if ((firstRouting && import.meta.server) || (import.meta.client && to.path !== from.path)) {
				// @todo translate route parameters to filters if there are any
				await applyRoute(to, { pinia: context.$pinia });
			}
		});

		// Keep routing store in sync with the actual router lifecycle.
		// `afterEach` runs once navigation is confirmed, avoiding UI flashes where the header
		// reflects the target route while the old page is still on screen.
		context.$router.afterEach(to => {
			void routingStore.setActiveRoute(to);
		});

		// Initial sync (covers first render / hydration where `afterEach` may not have run yet).
		void routingStore.setActiveRoute(context.$router.currentRoute.value);

		if (!userStore.onboardingStatus) {
			loadOnBoardingStatus(languageStore.webLocale, url);
		}

		s = t();
		await Promise.all(parallelFetches);
		phase('await-parallel-fetches', s);

		if (import.meta.server) {
			phase('plugin-total', setupStart);
			const event = nuxtApp.ssrContext?.event;
			if (event) {
				// Expose phases for server/plugins/prometheus.ts to observe in `afterResponse`.
				event.context.ssrPhases = phases;
				// Render time = wall clock between setup() returning and Vue SSR finishing.
				const renderStart = t();
				const renderStartMs = Number(renderStart) / 1e6;
				nuxtApp.hook('app:rendered', () => {
					const renderEnd = t();
					const renderMs = Number(renderEnd - renderStart) / 1e6;
					const renderEndMs = Number(renderEnd) / 1e6;
					phases['render'] = renderMs;

					// Split `render` into time blocked on GraphQL (union of in-flight intervals,
					// clamped to the render window so overlapping ops aren't double-counted) vs.
					// everything else (CPU / renderToString). `render-graphql-max` distinguishes a
					// single slow upstream query (max ≈ wait) from a waterfall (wait ≫ max).
					const gqlWaitMs = mergedDurationWithin(gqlIntervals, renderStartMs, renderEndMs);
					phases['render-graphql-wait'] = gqlWaitMs;
					phases['render-cpu'] = Math.max(0, renderMs - gqlWaitMs);
					phases['render-graphql-max'] = maxIntervalWithin(gqlIntervals, renderStartMs, renderEndMs);
				});
			}
		}
	},
});

async function checkHydrationBails() {
	if ('hydrationBailed' in window && Array.isArray(window.hydrationBailed) && window.hydrationBailed.length > 0) {
		const bails = [...window.hydrationBailed] as { cause: string; where: string; detail: { parent: any } }[];
		window.hydrationBailed = [];
		// fire hydration bails to sentry
		for (const bail of bails) {
			const { cause, where, detail } = bail;
			const title = where
				.split(' ')
				// only take last 3 portions of css selectors (-5 because " > " are also present)
				.slice(-5)
				// remove :nth-child() from selector to better group issues
				.map(item => item.split(':')[0])
				.join(' ');
			const error = new Error(cause);
			// Vnodes have additional information attached to the Vue component
			const parentComponentName = ((detail as any).VNodes?.[0]?.context?.$vnode.tag || 'none')
				.split('-')
				.slice(-1)
				.join('');
			error.name = `[HydrationBail] ${parentComponentName} ${title}`;
			console.error('[HydrationBail]', { cause, where, detail, parent: parentComponentName });
			TrackingHelper.trackEvent('performance_metrics', {
				action: 'measure',
				property: 'hydration',
				label: `${parentComponentName}###${where}`,
				nonInteraction: true,
			});
			captureMessageForSentry(`[HydrationBail]`, { error, where, parent: parentComponentName }, 'error');
		}
	}
}
/**
 * Generates a valid JustWatch ID if needed and sets
 * it in a cookie for the rest of the app to use.
 *
 * Returns true a JW ID was generated. Meaning this
 * is an SEO user.
 */
function initJwId(graphqlUrl: string, domain: string): boolean {
	if (import.meta.server) {
		const jwIdCookie = useCookie(JWID_COOKIE);

		if (jwIdCookie.value) return false;

		const userCookie =
			useCookie<Record<string, unknown>>('jw_user').value || useCookie<Record<string, unknown>>('jw/user').value;

		if (!userCookie) return true;

		return !userCookie?.jwLoginId && !userCookie?.jwId;
	}

	if (document.cookie.includes(JWID_COOKIE)) return false;

	const jwId = generateJwId();

	fetch(graphqlUrl, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({
			query: `mutation RegisterDeviceId { registerDeviceId(input: "${jwId}") { deviceId } }`,
		}),
	})
		.then(resp => (resp?.ok ? null : resp.json()))
		.then(json => {
			const error = (json?.errors ?? [])[0];
			if (error) throw error;
		})
		.catch(error => {
			captureMessageForSentry(
				'[Fetch GraphQL RegisterDeviceId]:',
				{ error, where: '[main.common.ts]: initJwId' },
				'error'
			);
		});

	// Delete any existing subdomain-specific jw_id cookie first
	document.cookie = `${JWID_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; SameSite=None`;

	// Set domain to share jw_id cookie across all subdomains (www, guides, sports, etc.)
	// In development, omit domain parameter for localhost compatibility
	const domainParam = process.env.NODE_ENV === 'development' ? '' : `.${domain}`;

	document.cookie =
		`${JWID_COOKIE}=${jwId}` +
		`; expires=${new Date(+new Date() + MAX_AGE).toUTCString()}` +
		`; path=/; secure; SameSite=None; Domain=${domainParam}`;

	return true;
}

/**
 * Apollo link that records each operation's full in-flight wall-clock (absolute hrtime ms)
 * into `intervals`. Placed at the front of the server chain so `forward()` wraps the HTTP
 * link — the timed window therefore covers the entire upstream round-trip. The pipe `map`
 * fires on the response emission (incl. GraphQL errors under errorPolicy:'all'); transport
 * failures skip it, which is acceptable for timing.
 */
function makeGraphqlTimingLink(intervals: Array<[number, number]>): ApolloLink {
	return new ApolloLink((operation, forward) => {
		const startMs = Number(process.hrtime.bigint()) / 1e6;
		return forward(operation).pipe(
			map(result => {
				intervals.push([startMs, Number(process.hrtime.bigint()) / 1e6]);
				return result;
			})
		);
	});
}

/**
 * Total wall-clock within [windowStart, windowEnd] during which at least one interval was
 * active. Intervals are clamped to the window and merged so overlapping GraphQL operations
 * are counted once — this is the time `render` was genuinely blocked on the network.
 */
function mergedDurationWithin(intervals: Array<[number, number]>, windowStart: number, windowEnd: number): number {
	const clamped = intervals
		.map(([s, e]) => [Math.max(s, windowStart), Math.min(e, windowEnd)] as [number, number])
		.filter(([s, e]) => e > s)
		.sort((a, b) => a[0] - b[0]);

	let total = 0;
	let curStart = -1;
	let curEnd = -1;
	for (const [s, e] of clamped) {
		if (s > curEnd) {
			if (curEnd > curStart) total += curEnd - curStart;
			curStart = s;
			curEnd = e;
		} else if (e > curEnd) {
			curEnd = e;
		}
	}
	if (curEnd > curStart) total += curEnd - curStart;
	return total;
}

/** Longest single operation overlapping the render window (clamped to it). */
function maxIntervalWithin(intervals: Array<[number, number]>, windowStart: number, windowEnd: number): number {
	let max = 0;
	for (const [s, e] of intervals) {
		const dur = Math.min(e, windowEnd) - Math.max(s, windowStart);
		if (dur > max) max = dur;
	}
	return max;
}
